import React, { useEffect, useState } from "react";
import {
  addImages,
  deleteImage,
  getAllImages,
  uploadImages,
} from "../../actions/admin";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  styled,
  Button,
  Backdrop,
  CircularProgress,
  Card,
  CardMedia,
  Chip,
  Fade,
  Paper,
} from "@mui/material";
import {
  DeleteForever,
  CloudUpload as CloudUploadIcon,
  Collections,
  Add,
  Image as ImageIcon,
} from "@mui/icons-material";
import Select from "react-select";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const StyledCard = styled(Card)(({ theme }) => ({
  position: "relative",
  borderRadius: "16px",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
    "& .delete-overlay": {
      opacity: 1,
    },
  },
}));

const DeleteOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  right: 0,
  background:
    "linear-gradient(135deg, rgba(244, 67, 54, 0.9), rgba(233, 30, 99, 0.9))",
  borderRadius: "0 16px 0 16px",
  opacity: 0,
  transition: "opacity 0.3s ease",
  backdropFilter: "blur(10px)",
});

const UploadArea = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
  borderRadius: "16px",
  padding: "32px 24px",
  textAlign: "center",
  border: "3px dashed rgba(255, 255, 255, 0.5)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  minHeight: "180px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 10px 30px rgba(79, 70, 229, 0.3)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 20px 40px rgba(79, 70, 229, 0.4)",
    border: "3px dashed rgba(255, 255, 255, 0.8)",
    background: "linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: 700,
  marginBottom: "24px",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -8,
    left: 0,
    width: "60px",
    height: "4px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "2px",
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  fontWeight: 600,
  fontSize: "14px",
  padding: "8px 4px",
  marginBottom: "16px",
  "& .MuiChip-icon": {
    color: "white",
  },
}));

const StyledSelect = styled(Select)({
  "& .css-13cymwt-control": {
    borderRadius: "12px",
    border: "2px solid #e0e7ff",
    boxShadow: "none",
    "&:hover": {
      border: "2px solid #667eea",
    },
  },
  "& .css-13cymwt-control--is-focused": {
    border: "2px solid #667eea",
    boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.2)",
  },
});

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "12px 32px",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "16px",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "linear-gradient(135deg, #5a6fd8 0%, #6b4190 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 12px 32px rgba(102, 126, 234, 0.4)",
  },
}));

const Images = () => {
  const { token } = useSelector((state) => state.userSlice);
  const dispatch = useDispatch();
  const { images } = useSelector((state) => state.appSlice);
  const [loading, setLoading] = useState(false);
  const [imagesUploading, setImagesUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageType, setImageType] = useState(null);

  const imageTypes = [
    {
      label: "Banner",
      value: "banner",
    },
    {
      label: "Sport Event",
      value: "sport_event",
    },
    {
      label: "Birthday Party",
      value: "birthday_party",
    },
    {
      label: "Corporate Event",
      value: "corporate_event",
    },
  ];

  const uploadImage = async (imagesToUpload) => {
    const formdata = new FormData();

    for (let i = 0; i < imagesToUpload.length; i++) {
      formdata.append(`image`, imagesToUpload[i]);
    }

    setImagesUploading(true);
    const resp = await uploadImages({
      token,
      data: formdata,
    });
    setUploadedImages((curr) => [...curr, ...(resp?.images || [])]);
    setImagesUploading(false);
  };

  const deleteImageFromDB = async (id) => {
    setLoading(true);
    await deleteImage({ token, id });
    dispatch(getAllImages());
    setLoading(false);
  };

  const addImagesToDB = async () => {
    setLoading(true);
    const data = {
      images: uploadedImages.map((img) => ({
        image_type: imageType.value,
        url: img.url,
        filename: img.filename,
      })),
    };
    await addImages({ token, data });
    dispatch(getAllImages());
    setLoading(false);
    setUploadedImages([]);
    setImageType(null);
  };

  useEffect(() => {
    dispatch(getAllImages());
  }, []);

  const renderImageSection = (sectionImages, title, icon) => {
    if (!sectionImages?.length) return null;

    return (
      <Fade in={true} timeout={600}>
        <Box sx={{ mb: 4 }}>
          <CategoryChip icon={icon} label={title} variant="filled" />
          <Grid container spacing={3}>
            {sectionImages.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image._id}>
                <Fade
                  in={true}
                  timeout={800}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <StyledCard>
                    <CardMedia
                      component="img"
                      height="200"
                      image={image.url}
                      alt={`${title} image`}
                      sx={{
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                    <DeleteOverlay className="delete-overlay">
                      <IconButton
                        onClick={() => deleteImageFromDB(image._id)}
                        sx={{
                          color: "white",
                          m: 1,
                          "&:hover": {
                            background: "rgba(255, 255, 255, 0.2)",
                          },
                        }}
                      >
                        <DeleteForever />
                      </IconButton>
                    </DeleteOverlay>
                  </StyledCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Fade>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        p: 4,
      }}
    >
      <Backdrop
        open={loading}
        sx={{
          zIndex: 1010,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#667eea",
              mb: 2,
            }}
          />
          <Typography variant="h6" sx={{ color: "#667eea", fontWeight: 600 }}>
            Processing...
          </Typography>
        </Box>
      </Backdrop>

      <Grid container spacing={4}>
        {/* All Images Section */}
        <Grid item xs={12} lg={8}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "24px",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              minHeight: "600px",
            }}
          >
            <SectionTitle
              variant="h4"
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Collections sx={{ fontSize: "36px", color: "#667eea" }} />
              Image Gallery
            </SectionTitle>

            <Box sx={{ mt: 4 }}>
              {renderImageSection(images.banner, "Banner", <ImageIcon />)}
              {renderImageSection(
                images.corporateEvent,
                "Corporate Event",
                <ImageIcon />,
              )}
              {renderImageSection(
                images.sportEvent,
                "Sport Event",
                <ImageIcon />,
              )}
              {renderImageSection(
                images.weddingEvent,
                "Wedding Event",
                <ImageIcon />,
              )}

              {!images.banner?.length &&
                !images.corporateEvent?.length &&
                !images.sportEvent?.length &&
                !images.weddingEvent?.length && (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 8,
                      color: "#9ca3af",
                    }}
                  >
                    <Collections
                      sx={{ fontSize: "80px", mb: 2, opacity: 0.3 }}
                    />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No images found
                    </Typography>
                    <Typography variant="body2">
                      Upload some images to get started
                    </Typography>
                  </Box>
                )}
            </Box>
          </Paper>
        </Grid>

        {/* Upload Section */}
        <Grid item xs={12} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "24px",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              height: "fit-content",
              position: "sticky",
              top: "20px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
            }}
          >
            <SectionTitle
              variant="h4"
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Add sx={{ fontSize: "36px", color: "#667eea" }} />
              Add Images
            </SectionTitle>

            <Box sx={{ mt: 4 }}>
              <UploadArea
                component="label"
                elevation={0}
                sx={{ cursor: imagesUploading ? "not-allowed" : "pointer" }}
              >
                <input
                  type="file"
                  multiple
                  onChange={(e) => uploadImage(e.target.files)}
                  style={{ display: "none" }}
                  disabled={imagesUploading}
                />
                {imagesUploading ? (
                  <Box sx={{ color: "white" }}>
                    <CircularProgress
                      sx={{
                        color: "white",
                        mb: 2,
                      }}
                      size={50}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Uploading Images...
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                      Please wait while we process your files
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ color: "white" }}>
                    <CloudUploadIcon
                      sx={{ fontSize: "64px", mb: 2, opacity: 0.9 }}
                    />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      Drop Files Here
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 400, mb: 2, opacity: 0.8 }}
                    >
                      or click to browse
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.7, fontSize: "14px" }}
                    >
                      Support for multiple image formats
                    </Typography>
                  </Box>
                )}
              </UploadArea>

              {uploadedImages?.length > 0 && (
                <Fade in={true} timeout={600}>
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: "#374151",
                      }}
                    >
                      Preview ({uploadedImages.length} images)
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {uploadedImages.map((image, index) => (
                        <Grid item xs={6} key={index}>
                          <Card
                            sx={{ borderRadius: "12px", overflow: "hidden" }}
                          >
                            <CardMedia
                              component="img"
                              height="100"
                              image={image.url}
                              alt={`Preview ${index}`}
                              sx={{ objectFit: "cover" }}
                            />
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: "#374151",
                      }}
                    >
                      Select Category
                    </Typography>
                    <StyledSelect
                      options={imageTypes}
                      onChange={(val) => setImageType(val)}
                      value={imageType}
                      placeholder="Choose image type..."
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "48px",
                        }),
                      }}
                    />

                    <ActionButton
                      variant="contained"
                      fullWidth
                      disabled={!imageType}
                      onClick={addImagesToDB}
                      sx={{ mt: 3 }}
                    >
                      Add Images to Gallery
                    </ActionButton>
                  </Box>
                </Fade>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Images;
